import React from "react";
import {
  Modal,
  Box,
  SpaceBetween,
  ColumnLayout,
  StatusIndicator,
  Link,
  Tabs,
  Container,
  Header,
  Button,
  CopyToClipboard,
  Alert,
} from "@cloudscape-design/components";
import { WorkflowExecution, WorkflowStatus } from "../../services";
import { JsonViewer } from "./JsonViewer";
import { TruncatedText } from "./TruncatedText";
import { DateFormatter, StatusMapper, AwsUrlBuilder, JsonFormatter } from "../../utils";
import { TimeDisplayMode } from "../../utils/DateFormatter";
import { LABELS } from "../../constants";

interface WorkflowDetailModalProps {
  workflow: WorkflowExecution | null;
  visible: boolean;
  onDismiss: () => void;
  onRedrive?: (arn: string) => void;
  timeMode?: TimeDisplayMode;
}

export const WorkflowDetailModal: React.FC<WorkflowDetailModalProps> = ({
  workflow,
  visible,
  onDismiss,
  onRedrive,
  timeMode = "local",
}) => {
  if (!workflow) {
    return null;
  }

  const inputData = JsonFormatter.parse(workflow.input);
  const outputData = workflow.output ? JsonFormatter.parse(workflow.output) : null;
  const canRedrive = workflow.status === WorkflowStatus.FAILED;

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header={
        <Header
          variant="h2"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Link external href={AwsUrlBuilder.buildStepFunctionsUrl(workflow.executionArn)}>
                {LABELS.OPEN_IN_CONSOLE}
              </Link>
              {canRedrive && onRedrive && (
                <Button variant="primary" onClick={() => onRedrive(workflow.executionArn)}>
                  {LABELS.REDRIVE_WORKFLOW}
                </Button>
              )}
            </SpaceBetween>
          }
        >
          {workflow.workflowName}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container header={<Header variant="h3">{LABELS.EXECUTION_DETAILS}</Header>}>
          <ColumnLayout columns={4} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">{LABELS.STATUS}</Box>
              <StatusIndicator type={StatusMapper.mapWorkflowStatus(workflow.status)}>{workflow.status}</StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">{LABELS.STARTED}</Box>
              <Box>{DateFormatter.formatDate(workflow.startDate, LABELS.EMPTY_VALUE, timeMode)}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">{LABELS.COMPLETED}</Box>
              <Box>{DateFormatter.formatDate(workflow.stopDate, LABELS.EMPTY_VALUE, timeMode)}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">{LABELS.DURATION}</Box>
              <Box>{DateFormatter.formatDuration(workflow.startDate, workflow.stopDate, LABELS.EXECUTION_IN_PROGRESS)}</Box>
            </div>
          </ColumnLayout>
        </Container>

        <Container header={<Header variant="h3">{LABELS.EXECUTION_ARN}</Header>}>
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Box variant="code" fontSize="body-s">
              {workflow.executionArn}
            </Box>
            <CopyToClipboard
              copyButtonAriaLabel={LABELS.COPY_JSON}
              copySuccessText={LABELS.COPIED}
              copyErrorText={LABELS.ERROR}
              textToCopy={workflow.executionArn}
            />
          </SpaceBetween>
        </Container>

        {workflow.workflowError && (
          <Alert type="error" header={LABELS.EXECUTION_ERROR}>
            <SpaceBetween size="s">
              <div>
                <Box variant="awsui-key-label">{LABELS.ERROR}</Box>
                <Box>{workflow.workflowError.error}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{LABELS.CAUSE}</Box>
                <TruncatedText text={workflow.workflowError.cause} maxLength={500} />
              </div>
            </SpaceBetween>
          </Alert>
        )}

        <Tabs
          tabs={[
            {
              id: "input",
              label: LABELS.INPUT,
              content: <JsonViewer data={inputData} emptyMessage={LABELS.NO_INPUT_DATA} maxHeight={400} />,
            },
            {
              id: "output",
              label: LABELS.OUTPUT,
              content: (
                <JsonViewer
                  data={outputData}
                  emptyMessage={workflow.status === WorkflowStatus.RUNNING ? LABELS.EXECUTION_IN_PROGRESS : LABELS.NO_OUTPUT_DATA}
                  maxHeight={400}
                />
              ),
              disabled: !outputData && workflow.status !== WorkflowStatus.RUNNING,
            },
          ]}
        />
      </SpaceBetween>
    </Modal>
  );
};
